import { request } from "./http";

function encode(value) {
  return encodeURIComponent(String(value ?? "").trim());
}

function asObject(value) {
  return value && typeof value === "object" ? value : {};
}

function normalizeTargetType(value) {
  return String(value || "")
    .trim()
    .toUpperCase()
    .replace(/[^A-Z0-9_]+/g, "_")
    .replace(/^_+|_+$/g, "");
}

function canTryFallback(error) {
  const status = Number(error?.status ?? error?.response?.status ?? 0);
  return status === 404 || status === 405 || status === 501;
}

function buildPayload(raw = {}) {
  const source = asObject(raw);
  const targetType = normalizeTargetType(source.targetType);
  const targetId = String(source.targetId ?? "").trim();
  const reasonCode = String(source.reasonCode || source.reason || "OTHER").trim().toUpperCase();
  const reasonText = String(source.reasonText || source.reasonLabel || source.reason || "").trim();
  const details = String(source.details || "").trim();
  const title = String(source.title || "").trim();
  const contentSnippet = String(source.contentSnippet || source.snippet || "").trim().slice(0, 800);
  const url = String(source.url || (typeof window !== "undefined" ? window.location.href : "")).trim();

  const targetNumericId = Number(targetId);

  return {
    targetType,
    contentType: targetType,
    entityType: targetType,
    targetId,
    contentId: targetId,
    entityId: targetId,
    ...(Number.isFinite(targetNumericId) ? { targetNumericId, contentNumericId: targetNumericId } : {}),
    reason: reasonCode,
    reasonCode,
    reasonText,
    details,
    title,
    contentSnippet,
    url,
    pageUrl: url,
    meta: source.meta && typeof source.meta === "object" ? source.meta : {},
  };
}

function specificReportPaths(targetType, targetId) {
  const id = encode(targetId);
  switch (normalizeTargetType(targetType)) {
    case "FORUM_TOPIC":
      return [`/api/forum/topics/${id}/report`];
    case "FORUM_COMMENT":
      return [`/api/forum/comments/${id}/report`];
    case "BLOG_POST":
      return [`/api/blog/posts/${id}/report`];
    case "BLOG_COMMENT":
      return [`/api/blog/comments/${id}/report`];
    case "PODCAST":
      return [`/api/podcasts/${id}/report`, `/api/public/podcasts/${id}/report`];
    case "PODCAST_EPISODE":
      return [`/api/podcasts/episodes/${id}/report`, `/api/public/podcasts/episodes/${id}/report`];
    case "PODCAST_COMMENT":
      return [`/api/podcasts/comments/${id}/report`, `/api/public/podcasts/comments/${id}/report`];
    case "LITERATURE_BOOK":
      return [`/api/literature/books/${id}/report`, `/api/public/literature/books/${id}/report`];
    case "LITERATURE_COMMENT":
      return [`/api/literature/comments/${id}/report`];
    case "PSYCHOLOGIST_PROFILE":
      return [`/api/psychologists/${id}/report`, `/api/public/psychologists/${id}/report`];
    case "SESSION":
      return [`/api/sessions/${id}/report`, `/api/group-sessions/${id}/report`];
    case "TEST":
      return [`/api/tests/${id}/report`];
    default:
      return [];
  }
}

const GENERIC_REPORT_PATHS = [
  "/api/reports",
  "/api/content-reports",
  "/api/moderation/reports",
  "/api/complaints",
];

export const REPORT_TARGET_TYPES = Object.freeze({
  FORUM_TOPIC: "FORUM_TOPIC",
  FORUM_COMMENT: "FORUM_COMMENT",
  BLOG_POST: "BLOG_POST",
  BLOG_COMMENT: "BLOG_COMMENT",
  PODCAST: "PODCAST",
  PODCAST_EPISODE: "PODCAST_EPISODE",
  PODCAST_COMMENT: "PODCAST_COMMENT",
  LITERATURE_BOOK: "LITERATURE_BOOK",
  LITERATURE_COMMENT: "LITERATURE_COMMENT",
  PSYCHOLOGIST_PROFILE: "PSYCHOLOGIST_PROFILE",
  USER: "USER",
  USER_PROFILE: "USER_PROFILE",
  SESSION: "SESSION",
  TEST: "TEST",
});

export const reportsApi = {
  async submitReport(rawPayload = {}) {
    const payload = buildPayload(rawPayload);
    if (!payload.targetType) throw new Error("Не указан тип контента для жалобы");
    if (!payload.targetId) throw new Error("Не указан объект жалобы");

    const paths = [
      ...specificReportPaths(payload.targetType, payload.targetId),
      ...GENERIC_REPORT_PATHS,
    ];

    let lastError = null;
    for (const path of paths) {
      try {
        return await request(path, {
          method: "POST",
          json: payload,
        });
      } catch (error) {
        if (!canTryFallback(error)) throw error;
        lastError = error;
      }
    }

    if (lastError) throw lastError;
    throw new Error("Не найден эндпоинт для отправки жалобы");
  },

  submitPsychologistAccessRequest({ userId, details = "" } = {}) {
    const normalizedUserId = Number(userId);
    if (!Number.isFinite(normalizedUserId) || normalizedUserId <= 0) {
      throw new Error("Не удалось определить пользователя для отправки заявки");
    }
    const normalizedDetails = String(details || "").trim();
    return this.submitReport({
      targetType: REPORT_TARGET_TYPES.USER,
      targetId: normalizedUserId,
      reasonCode: "PSYCHOLOGIST_ACCESS_REQUEST",
      reasonText: "Заявка на доступ к роли психолога",
      details: normalizedDetails || "Пользователь отправил заявку на доступ к роли психолога.",
    });
  },
};
